import { Languages } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LANGUAGES } from '../i18n/config'
import { api } from '../services/api'

/**
 * Language picker dropdown. Persists choice in two places:
 *
 *   1. localStorage (key "vynaemr-language") — handled
 *      automatically by i18next's LanguageDetector cache config,
 *      so the same language survives reloads on this device.
 *   2. The server, via `PUT /auth/profile { preferredLanguage }`,
 *      so a returning user on a different browser/device sees
 *      their previously-chosen language — but ONLY when a session
 *      exists. On the login page there is no access token, so the
 *      PUT would 401 and the axios error interceptor would toast
 *      `errors:unexpected`; skipping the call beats catching it.
 *
 * Used on the login page (alongside ThemeToggle) and in the
 * authenticated TopBar. Keep it accessible: the <select> gets the
 * visible label via aria-label, and the icon is purely decorative.
 */
export default function LanguageSwitcher() {
  const { i18n } = useTranslation('common')

  const handleChange = async (code: string) => {
    await i18n.changeLanguage(code)
    // Server persistence so the preference roams across
    // browsers/devices — skipped when there is no session (login
    // page): the PUT would 401 and the global error interceptor
    // toasts before this catch ever sees it. The localStorage cache
    // (LanguageDetector) keeps the local choice regardless; a stale
    // or offline failure still lands here and stays silent.
    if (!localStorage.getItem('access_token')) return
    try {
      await api.auth.updateProfile({ preferredLanguage: code })
    } catch (err) {
      console.warn('Failed to persist language preference to server:', err)
    }
  }

  return (
    <label className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
      <Languages className="h-4 w-4 shrink-0" aria-hidden="true" />
      <select
        value={i18n.resolvedLanguage ?? i18n.language}
        onChange={(e) => {
          void handleChange(e.target.value)
        }}
        aria-label="Select language"
        className="bg-transparent text-sm focus:outline-hidden cursor-pointer"
      >
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code} className="bg-background text-foreground">
            {lang.label}
          </option>
        ))}
      </select>
    </label>
  )
}
